import { Box, Typography } from '@material-ui/core';
import { FieldError } from '@spike/model';
import { PetCustomPriceUpdatePayload } from '@spike/pet-model';
import useNonInitialEffect from '@versiondos/hooks';
import { ConfirmDialog } from 'components/UI';
import PriceField from 'components/UI/V3/Forms/PriceField';
import useIsMobile from 'hooks/useIsMobile';
import { FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { PetsStatus } from '@spike/pets-action';
import { CustomPriceDisplayRow } from './customPriceRows';
import styles from './EditCustomPriceModal.module.scss';

interface Props {
    open: boolean;
    row: CustomPriceDisplayRow;
    processing?: boolean;
    onCancel: () => void;
    onSave: (payload: PetCustomPriceUpdatePayload) => void;
}

export const EditCustomPriceModal: FunctionComponent<Props> = ({ open, row, processing, onCancel, onSave }) => {
    const petsStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);
    const isMobile = useIsMobile();
    const [savedPrice, setSavedPrice] = useState<number | undefined>(row.price);
    const [errors, setErrors] = useState<Array<FieldError>>([]);

    useNonInitialEffect(() => {
        if (petsStatus === PetsStatus.UpdateCustomPriceSuccess) {
            onCancel();
        }
    }, [petsStatus]);

    const buildPayload = (price: number): PetCustomPriceUpdatePayload => {
        if (row.priceType === 'pickup') {
            return { pickupPrice: price };
        }
        if (row.priceType === 'dropoff') {
            return { dropoffPrice: price };
        }
        return { price };
    };

    const handleSave = () => {
        if (savedPrice == null || !Number.isFinite(savedPrice) || savedPrice < 0) {
            setErrors([{ fieldName: 'savedPrice', errorMessage: 'Enter a valid price.' }]);
            return;
        }
        setErrors([]);
        onSave(buildPayload(savedPrice));
    };

    return (
        <ConfirmDialog
            id="pet_custom_price_edit_"
            open={open}
            className={styles.editModal}
            title={<span className={styles.editTitle}>Edit Custom Price</span>}
            isForm={true}
            isSuccess={true}
            elementAlignment="left"
            cancelButtonLabel={isMobile ? 'Cancel' : 'No'}
            confirmButtonLabel="Save"
            processing={processing}
            onCancel={onCancel}
            onConfirm={handleSave}
            question={
                <Box className={styles.form}>
                    <Box className={styles.field}>
                        <Typography className={styles.label}>Service</Typography>
                        <Typography className={styles.serviceValue}>{row.serviceName}</Typography>
                    </Box>
                    <Box className={styles.field}>
                        <PriceField
                            id="pet_custom_price_saved_price"
                            name="savedPrice"
                            label="Saved Price"
                            className={styles.priceInput}
                            value={savedPrice}
                            scale={2}
                            precision={6}
                            max={999999.99}
                            placeholder="0.00"
                            errors={errors}
                            disabled={processing}
                            onChange={value => {
                                setSavedPrice(value);
                                setErrors([]);
                            }}
                            onEnterPressed={handleSave}
                        />
                    </Box>
                </Box>
            }
        />
    );
};

export default EditCustomPriceModal;
